import { z } from 'zod'
import { CardAttachmentIdSchema, CompanyIdSchema } from '../ids'
import { CardBaseSchema, CardGalleryFileSchema, CardLinkSchema } from './common'

export const CardColorSchema = z.union([
  z.enum(['navy', 'slate', 'blue', 'tan']),
  z.string().regex(/^#[0-9a-fA-F]{6}$/),
])
export type CardColor = z.infer<typeof CardColorSchema>

export const CompanyCardThemeSchema = z.object({
  bannerColor: CardColorSchema.optional(),
  accentColor: CardColorSchema.optional(),
  textColor: CardColorSchema.optional(),
})
export type CompanyCardTheme = z.infer<typeof CompanyCardThemeSchema>

export const CompanyCardSectionsSchema = z.object({
  trust: z.boolean(),
  gallery: z.boolean(),
  reviews: z.boolean(),
  cta: z.boolean(),
  socials: z.boolean(),
  feedbackInbox: z.boolean().optional(),
})
export type CompanyCardSections = z.infer<typeof CompanyCardSectionsSchema>

export const CardMessageTemplateSchema = z.object({
  name: z.string(),
  tag: z.string(),
  body: z.string(),
})
export type CardMessageTemplate = z.infer<typeof CardMessageTemplateSchema>

export const CompanyCardSchema = z.object({
  ...CardBaseSchema.shape,
  entityType: z.literal('company'),
  entityId: CompanyIdSchema,
  links: z.array(CardLinkSchema).optional(),
  attachmentIds: z.array(CardAttachmentIdSchema).optional(),
  attachments: z.array(CardGalleryFileSchema).optional(),
  theme: CompanyCardThemeSchema.optional(),
  sections: CompanyCardSectionsSchema.optional(),
  imageSource: z.enum(['company', 'rep', 'both']).optional(),
  maxImagesPerCard: z.number().int().min(1).max(12).optional(),
  starThreshold: z.int().min(3).max(5).optional(),
  teamInboxEmail: z.email().optional(),
  notifyManager: z.boolean().optional(),
  messageTemplates: z.array(CardMessageTemplateSchema).optional(),
  placeId: z.string().optional(),
})
export type CompanyCard = z.infer<typeof CompanyCardSchema>

export const DecoratedCompanyCardSchema = z.object({
  ...CompanyCardSchema.shape,
  placeLabel: z.string().optional(),
})
export type DecoratedCompanyCard = z.infer<typeof DecoratedCompanyCardSchema>

export const ReviewSchema = z.object({
  rating: z.number(),
  relativePublishTime: z.string().meta({ example: 'a month ago' }),
  text: z.string(),
  author: z.object({
    name: z.string(),
    avatarUrl: z.string(),
  }),
})
export type Review = z.infer<typeof ReviewSchema>

export const PublicCompanySchema = z.object({
  ...CompanyCardSchema.pick({
    slug: true,
    entityType: true,
    links: true,
    attachmentIds: true,
    theme: true,
    sections: true,
    imageSource: true,
    maxImagesPerCard: true,
    starThreshold: true,
    placeId: true,
  }).shape,
  feedbackInboxEnabled: z.boolean().optional(),
  name: z.string(),
  iconUrl: z.string().optional(),
  iconBlurhash: z.string().optional(),
  rating: z.number().optional(),
  ratingCount: z.number().optional(),
  reviews: ReviewSchema.array().optional(),
})
export type PublicCompany = z.infer<typeof PublicCompanySchema>
